class IframeManager {
  constructor() {
    this.iframe = null;
    this.isReady = false;
    this.messageHandlers = new Map();
    this.pendingRequests = new Map();
    this.requestId = 0;
    
    this.setupMessageListener();
  }
  
  setupMessageListener() {
    window.addEventListener('message', (event) => {
      const { type, provider, payload } = event.data;
      
      if (!type || !type.startsWith('PLIVO_IFRAME_')) {
        return;
      }
      
      console.log('[IframeManager] Received message:', type, payload);
      
      if (type === 'PLIVO_IFRAME_CONSOLE_LOG') {
        console.log('[PlivoIframe]', payload?.message || payload);
        return;
      }
      
      if (type === 'PLIVO_IFRAME_CONSOLE_ERROR') {
        console.error('[PlivoIframe]', payload?.message || payload);
        return;
      }
      
      if (type === 'PLIVO_IFRAME_READY') {
        this.isReady = true;
        console.log('[IframeManager] Iframe is ready');
        this.resolveAllPending('READY', { success: true });
        return;
      }
      
      const handlers = this.messageHandlers.get(type);
      if (handlers) {
        console.log(`[IframeManager] Found ${handlers.size} handler(s) for ${type}`);
        handlers.forEach(handler => {
          console.log(`[IframeManager] Calling handler for ${type}`);
          handler(payload);
        });
      } else {
        console.log(`[IframeManager] No handlers registered for ${type}`);
      }
      
      if (payload && payload.requestId !== undefined) {
        this.resolvePending(payload.requestId, payload);
      }
    });
  }
  
  createIframe(src = '/plivo-iframe.html') {
    if (this.iframe) {
      console.log('[IframeManager] Iframe already exists');
      return this.iframe;
    }
    
    console.log('[IframeManager] Creating iframe with src:', src);
    
    this.iframe = document.createElement('iframe');
    this.iframe.src = src;
    this.iframe.style.display = 'none';
    this.iframe.style.position = 'absolute';
    this.iframe.style.width = '0';
    this.iframe.style.height = '0';
    this.iframe.style.border = 'none';
    
    this.iframe.onload = () => {
      console.log('[IframeManager] Iframe loaded successfully');
    };
    
    this.iframe.onerror = (error) => {
      console.error('[IframeManager] Iframe failed to load:', error);
    };
    
    document.body.appendChild(this.iframe);
    console.log('[IframeManager] Iframe appended to body');
    
    return this.iframe;
  }
  
  async waitForReady(timeout = 10000) {
    if (this.isReady) {
      return true;
    }
    
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        reject(new Error('Iframe ready timeout'));
      }, timeout);
      
      const checkReady = () => {
        if (this.isReady) {
          clearTimeout(timer);
          resolve(true);
        } else {
          setTimeout(checkReady, 100);
        }
      };
      
      checkReady();
    });
  }
  
  sendMessage(type, payload = {}) {
    if (!this.iframe) {
      throw new Error('Iframe not created');
    }
    
    const requestId = this.requestId++;
    const message = {
      type,
      payload: {
        ...payload,
        requestId
      }
    };
    
    console.log('[IframeManager] Sending message:', type, payload);
    this.iframe.contentWindow.postMessage(message, '*');
    
    return requestId;
  }
  
  async sendMessageWithResponse(type, payload = {}, timeout = 10000) {
    const requestId = this.sendMessage(type, payload);
    
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pendingRequests.delete(requestId);
        reject(new Error(`Message response timeout: ${type}`));
      }, timeout);
      
      this.pendingRequests.set(requestId, {
        resolve: (data) => {
          clearTimeout(timer);
          resolve(data);
        },
        reject: (error) => {
          clearTimeout(timer);
          reject(error);
        }
      });
    });
  }
  
  resolvePending(requestId, data) {
    const pending = this.pendingRequests.get(requestId);
    if (pending) {
      this.pendingRequests.delete(requestId);
      pending.resolve(data);
    }
  }
  
  resolveAllPending(type, data) {
    this.pendingRequests.forEach((pending) => {
      pending.resolve(data);
    });
    this.pendingRequests.clear();
  }
  
  on(eventType, handler) {
    if (!this.messageHandlers.has(eventType)) {
      this.messageHandlers.set(eventType, []);
    }
    this.messageHandlers.get(eventType).push(handler);
  }
  
  off(eventType, handler) {
    const handlers = this.messageHandlers.get(eventType);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }
  
  removeAllListeners(eventType) {
    if (this.messageHandlers.has(eventType)) {
      this.messageHandlers.delete(eventType);
    }
  }
  
  destroy() {
    if (this.iframe) {
      document.body.removeChild(this.iframe);
      this.iframe = null;
    }
    this.isReady = false;
    this.messageHandlers.clear();
    this.pendingRequests.clear();
  }
}

export default new IframeManager();

